import React, { useState, useMemo, useEffect } from 'react';
import { BrowserRouter, Routes, Route, useSearchParams } from 'react-router-dom';
import './App.css';

import { useAuth } from './hooks/useAuth';
import { useFirebaseData } from './hooks/useFirebaseData';
import { useUserRole } from './hooks/useUserRole';

import LandingPage from './components/LandingPage';
import PartnerProgramPage from './components/PartnerProgramPage';
import LoginPage from './components/LoginPage';
import Sidebar from './components/Sidebar';
import KPICards from './components/KPICards';
import InventoryTracker from './components/InventoryTracker';
import TransactionsTable from './components/TransactionsTable';
import RevenueChart from './components/RevenueChart';
import ExportButton from './components/ExportButton';
import FleetOverviewGrid from './components/FleetOverviewGrid';
import KioskDetailView from './components/KioskDetailView';
import TemperatureChart from './components/TemperatureChart';
import RefundQueuePanel from './components/RefundQueuePanel';
import PeakHoursChart from './components/PeakHoursChart';
import PromoControlPanel from './components/PromoControlPanel';
import ManageManagers from './components/ManageManagers';

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/partner-program" element={<PartnerProgramPage />} />
        <Route path="*" element={<MainApp />} />
      </Routes>
    </BrowserRouter>
  );
}

function MainApp() {
  const { user, loading: authLoading, authError, login, logout } = useAuth();
  const { inventory, orders, refundedIds, refundQueue, telemetry, tempHistory, loading: dataLoading, restockJuice } = useFirebaseData(user);
  const { role, kiosks: allowedKiosks, name: userName, roleLoading } = useUserRole(user);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [selectedKiosk, setSelectedKiosk] = useState(null);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  const [searchParams, setSearchParams] = useSearchParams();
  const showLogin = searchParams.get('login') === '1';

  // The public site (index.html defaults) is titled "Chill Ras" with the
  // brand-green theme color. Only the logged-in dashboard is "Fleet Command".
  useEffect(() => {
    const themeColorTag = document.querySelector('meta[name="theme-color"]');
    if (user) {
      document.title = 'Chill Ras Fleet Command';
      if (themeColorTag) themeColorTag.setAttribute('content', '#0d0d1a');
    } else {
      document.title = 'Chill Ras';
      if (themeColorTag) themeColorTag.setAttribute('content', '#0f3d20');
    }
  }, [user]);

  const isManager = role === 'manager';
  // Managers only ever see the Dashboard tab — Sidebar won't offer other
  // tabs, this is the defensive backstop in case activeTab ever ends up
  // set to something else.
  const effectiveTab = isManager ? 'dashboard' : activeTab;

  // Scope every data slice down to the manager's assigned kiosks. Admin
  // (allowedKiosks === null) gets the unfiltered originals.
  const scopedOrders = useMemo(() => {
    if (!isManager) return orders;
    return orders.filter((o) => (allowedKiosks || []).includes(o.kiosk_id));
  }, [isManager, orders, allowedKiosks]);

  const scopedInventory = useMemo(() => {
    if (!isManager) return inventory;
    return Object.fromEntries(Object.entries(inventory).filter(([k]) => (allowedKiosks || []).includes(k)));
  }, [isManager, inventory, allowedKiosks]);

  const scopedTelemetry = useMemo(() => {
    if (!isManager) return telemetry;
    return Object.fromEntries(Object.entries(telemetry).filter(([k]) => (allowedKiosks || []).includes(k)));
  }, [isManager, telemetry, allowedKiosks]);

  const scopedTempHistory = useMemo(() => {
    if (!isManager) return tempHistory;
    return Object.fromEntries(Object.entries(tempHistory).filter(([k]) => (allowedKiosks || []).includes(k)));
  }, [isManager, tempHistory, allowedKiosks]);

  const scopedRefundQueue = useMemo(() => {
    if (!isManager) return refundQueue;
    // Entries missing kiosk_id are excluded from a manager's view (safer
    // default) rather than shown — admin's view is unaffected.
    return Object.fromEntries(
      Object.entries(refundQueue).filter(([, v]) => v?.kiosk_id && (allowedKiosks || []).includes(v.kiosk_id))
    );
  }, [isManager, refundQueue, allowedKiosks]);

  // Auth + role loading state
  if (authLoading || (user && roleLoading)) {
    return (
      <div className="loading-screen">
        <div className="loading-spinner" />
        <p className="loading-text">Initializing Fleet Command...</p>
      </div>
    );
  }

  // Not authenticated - Show Landing Page for Razorpay compliance, or Login if requested
  if (!user) {
    if (showLogin) {
      return <LoginPage onLogin={login} authError={authError} onBack={() => setSearchParams({})} />;
    }
    return <LandingPage />;
  }

  const renderContent = () => {
    if (dataLoading) {
      return (
        <div className="content-loading">
          <div className="loading-spinner" />
          <p className="loading-text">Loading fleet data...</p>
        </div>
      );
    }

    switch (effectiveTab) {
      case 'dashboard':
        if (selectedKiosk) {
          return (
            <KioskDetailView
              kioskId={selectedKiosk}
              orders={scopedOrders}
              inventory={scopedInventory}
              telemetry={scopedTelemetry}
              tempHistory={scopedTempHistory}
              refundedIds={refundedIds}
              refundQueue={scopedRefundQueue}
              onRestock={restockJuice}
              onBack={() => setSelectedKiosk(null)}
            />
          );
        }
        return (
          <>
            <KPICards orders={scopedOrders} refundedIds={refundedIds} inventory={scopedInventory} />
            <FleetOverviewGrid
              telemetry={scopedTelemetry}
              inventory={scopedInventory}
              orders={scopedOrders}
              refundedIds={refundedIds}
              onSelectKiosk={setSelectedKiosk}
            />
            <RefundQueuePanel refundQueue={scopedRefundQueue} />
            <TransactionsTable orders={scopedOrders} refundedIds={refundedIds} refundQueue={scopedRefundQueue} />
          </>
        );
      case 'inventory':
        return <InventoryTracker inventory={inventory} onRestock={restockJuice} />;
      case 'transactions':
        return (
          <>
            <RefundQueuePanel refundQueue={refundQueue} />
            <TransactionsTable orders={orders} refundedIds={refundedIds} refundQueue={refundQueue} />
          </>
        );
      case 'analytics':
        return (
          <>
            <TemperatureChart tempHistory={tempHistory} />
            <RevenueChart orders={orders} refundedIds={refundedIds} />
          </>
        );
      case 'peakhours':
        return <PeakHoursChart orders={orders} />;
      case 'promo':
        return <PromoControlPanel />;
      case 'managers':
        return <ManageManagers inventory={inventory} />;
      default:
        return null;
    }
  };

  return (
    <div className="app-layout">
      <Sidebar
        activeTab={effectiveTab}
        onTabChange={setActiveTab}
        onLogout={logout}
        role={role}
        userName={userName}
        mobileOpen={mobileNavOpen}
        onCloseMobile={() => setMobileNavOpen(false)}
      />
      <main className="main-content">
        <header className="main-header">
          <button className="mobile-menu-btn" onClick={() => setMobileNavOpen(true)} aria-label="Open menu">☰</button>
          <div>
            <h1 className="main-title">
              {effectiveTab === 'dashboard'    && 'Fleet Command'}
              {effectiveTab === 'inventory'    && 'Inventory Management'}
              {effectiveTab === 'transactions' && 'Transaction History'}
              {effectiveTab === 'analytics'    && 'Analytics & Insights'}
              {effectiveTab === 'peakhours'    && 'Peak Hours Analysis'}
              {effectiveTab === 'promo'        && 'Promo Price Control'}
              {effectiveTab === 'managers'     && 'Manage Managers'}
            </h1>
            <p className="main-subtitle">
              {effectiveTab === 'dashboard'    && (isManager ? 'Real-time overview of your kiosks' : 'Real-time overview of all Chill Ras kiosks')}
              {effectiveTab === 'inventory'    && 'Monitor and restock juice levels across all kiosks'}
              {effectiveTab === 'transactions' && 'Browse and export order history'}
              {effectiveTab === 'analytics'    && 'Revenue trends, beverage popularity, and kiosk performance'}
              {effectiveTab === 'peakhours'    && 'Discover your busiest hours and days'}
              {effectiveTab === 'promo'        && 'Control promotional pricing across all kiosks remotely'}
              {effectiveTab === 'managers'     && 'Create and manage login access for kiosk managers'}
            </p>
          </div>
          <ExportButton orders={isManager ? scopedOrders : orders} />
        </header>
        <div className="main-body">
          {renderContent()}
        </div>
      </main>
    </div>
  );
}

export default App;
